import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Layout from "../../components/global/layout";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import swal from "sweetalert";
import { toast } from "react-toastify";
import ActionMenu from "../../components/common/ActionMenu";
import CommonPagination from "../../components/common/CommonPagination";
import FormControl from "../../components/common/FormControl";
// import {
//   MODULE_CONFIG,
//   getPortalUrl,
//   formatDate,
//   formatTime,
//   capitalize,
// } from "./shared";
import {
  MODULE_CONFIG,
  getPortalUrl,
  getTenantLogoUrl,
  formatDate,
  formatTime,
  capitalize,
} from "./shared";
import {
  FiSearch,
  FiPlus,
  FiEye,
  FiEdit3,
  FiTrash2,
  FiRotateCcw,
  FiX,
  FiExternalLink,
  FiCopy,
  FiCheckCircle,
  FiAlertOctagon,
  FiGlobe,
} from "react-icons/fi";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { hasPermission } from "../../utils/permission";

export default function TenantsList() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);

  // Dynamic RBAC Permission Checks
  const canCreate = hasPermission(user, "tenants", "create");
  const canEdit = hasPermission(user, "tenants", "edit");
  const canStatusChange = hasPermission(user, "tenants", "status_change");
  const canDelete = hasPermission(user, "tenants", "delete");

  // State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Filters & Pagination (Default 25 per page)
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Search input handler
  const handleSearchChange = (val) => {
    setSearchQuery(val);
    if (!val || !val.trim()) {
      loader(true);
      setDebouncedSearch("");
      setPage(1);
    }
  };

  // Debounce search input (350ms)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setDebouncedSearch("");
      return;
    }
    const handler = setTimeout(() => {
      loader(true);
      setDebouncedSearch(searchQuery.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Fetch tenants from API
  const fetchTenants = useCallback(
    async (showLoader = true) => {
      if (showLoader) loader(true);
      setLoading(true);

      try {
        const queryParams = new URLSearchParams();
        queryParams.append("page", String(page));
        queryParams.append("limit", String(limit || 25));
        queryParams.append("count", String(limit || 25));

        // Filter by lifecycle status
        if (statusFilter && statusFilter !== "ALL") {
          queryParams.append("status", statusFilter);
        }

        if (debouncedSearch && debouncedSearch.trim()) {
          queryParams.append("search", debouncedSearch.trim());
        }

        const endpoint = `${MODULE_CONFIG.endpoints.listing}?${queryParams.toString()}`;
        const res = await ApiClient.get(endpoint);

        if (res?.success) {
          const data = res?.data;
          const allRecords = Array.isArray(data?.tenants)
            ? data.tenants
            : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.docs)
            ? data.docs
            : Array.isArray(data?.list)
            ? data.list
            : Array.isArray(data)
            ? data
            : [];

          const dynamicTotal =
            Number(
              res?.total ??
              res?.totalDocs ??
              res?.count ??
              data?.total ??
              data?.totalDocs ??
              data?.count ??
              allRecords.length
            ) || 0;

          setTotalCount(dynamicTotal);

          // If the backend did not slice according to limit, slice on client
          const paginatedRecords =
            allRecords.length > (limit || 25)
              ? allRecords.slice((page - 1) * (limit || 25), page * (limit || 25))
              : allRecords;

          setItems(paginatedRecords);
        } else {
          setItems([]);
          setTotalCount(0);
        }
      } catch (err) {
        console.error("Failed to fetch tenants:", err);
        setItems([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
        if (showLoader) loader(false);
      }
    },
    [page, limit, statusFilter, debouncedSearch]
  );

  useEffect(() => {
    fetchTenants(true);
  }, [fetchTenants]);

  // Reset filters
  const isFilterActive = Boolean(
    searchQuery.trim() || (statusFilter && statusFilter !== "ALL")
  );

  const handleResetFilters = () => {
    loader(true);
    setSearchQuery("");
    setDebouncedSearch("");
    setStatusFilter("ALL");
    setPage(1);
  };

  // Copy text helper
  const handleCopy = (text, label = "URL") => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.info(`${label} copied to clipboard!`);
  };

  // Handle lifecycle status change
  const handleStatusChange = (item, newStatus) => {
    const id = item?.id || item?._id;
    const companyName = item?.company_name || item?.name || "Tenant";

    swal({
      title: `Change Status to ${capitalize(newStatus)}?`,
      text: `Are you sure you want to change the status of "${companyName}" to ${newStatus}?${
        newStatus === "SUSPENDED"
          ? " Suspended tenants are blocked by the tenant resolver."
          : ""
      }`,
      icon: newStatus === "SUSPENDED" ? "warning" : "info",
      buttons: ["Cancel", "Confirm Update"],
      dangerMode: newStatus === "SUSPENDED" || newStatus === "CANCELLED",
      className: "smallmodal",
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      try {
        const endpoint = MODULE_CONFIG.endpoints.statusChange(id);
        const res = await ApiClient.put(endpoint, { status: newStatus });
        if (res?.success) {
          toast.success(`Tenant status changed to ${newStatus}.`);
          fetchTenants(true);
        } else {
          toast.error(res?.message || "Failed to update tenant status.");
        }
      } catch (err) {
        console.error("Status update error:", err);
        toast.error("Failed to update status.");
      } finally {
        loader(false);
      }
    });
  };

  // Handle soft-delete tenant
  const handleDelete = (item) => {
    const id = item?.id || item?._id;
    const companyName = item?.company_name || item?.name || "Tenant";

    swal({
      title: "Delete Tenant Organization?",
      text: `Are you sure you want to delete "${companyName}"? This will deactivate all assigned users and disconnect QuickBooks.`,
      icon: "warning",
      buttons: ["Cancel", "Delete Tenant"],
      dangerMode: true,
      className: "smallmodal",
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      try {
        const endpoint = MODULE_CONFIG.endpoints.delete(id);
        const res = await ApiClient.delete(endpoint);
        if (res?.success) {
          toast.success("Tenant organization deleted successfully.");
          fetchTenants(true);
        } else {
          toast.error(res?.message || "Failed to delete tenant.");
        }
      } catch (err) {
        console.error("Delete error:", err);
        toast.error("Failed to delete tenant.");
      } finally {
        loader(false);
      }
    });
  };

  // Status badge helper
  const getStatusBadge = (statusKey) => {
    const key = (statusKey || "ACTIVE").toUpperCase();
    const config = MODULE_CONFIG.statusMap[key] || {
      label: capitalize(key),
      bgClass: "bg-slate-200 text-slate-900 border-slate-300 font-bold",
      dotClass: "bg-slate-600",
    };
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] uppercase tracking-wide border whitespace-nowrap shadow-2xs ${config.bgClass}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${config.dotClass}`} />
        {config.label}
      </span>
    );
  };

  return (
    <Layout>
      <div className="w-full max-w-full space-y-5 pb-12 antialiased">
        {/* ======================================================== */}
        {/* 1. TOP HEADER & FILTER BAR */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.03)] flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="space-y-0.5 min-w-0">
            <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight truncate">
              {MODULE_CONFIG.title}
            </h1>
            <p className="text-xs text-slate-700 font-semibold">
              {MODULE_CONFIG.subtitle}
            </p>
          </div>

          {/* Filter & Action Controls */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 flex-wrap shrink-0">
            {/* Search Input */}
            <div className="w-full sm:w-64 md:w-72">
              <div className="relative flex items-center group">
                <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500 group-focus-within:text-[#115B9D] transition-colors">
                  <FiSearch className="w-4 h-4" />
                </span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search company, subdomain, portal..."
                  className="w-full bg-white border-[1.5px] border-slate-500 rounded-xl pl-10 pr-9 h-10 text-xs sm:text-sm font-semibold text-slate-900 placeholder:text-slate-500 outline-none focus:outline-none focus:ring-0 focus:border-slate-500 shadow-box"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => handleSearchChange("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
                    title="Clear search"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Status Filter Dropdown */}
            <div className="w-full sm:w-44 shrink-0">
              <FormControl
                type="select"
                placeholder="Filter by Status"
                value={statusFilter}
                theme="search"
                options={MODULE_CONFIG.statusOptions}
                onChange={(val) => {
                  loader(true);
                  setStatusFilter(val || "ALL");
                  setPage(1);
                }}
              />
            </div>

            {/* Reset Filters Button */}
            <button
              type="button"
              onClick={handleResetFilters}
              disabled={!isFilterActive}
              className={`inline-flex items-center justify-center gap-1.5 px-3.5 h-10 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                isFilterActive
                  ? "bg-white hover:bg-slate-100 active:bg-slate-200 text-slate-800 hover:text-slate-950 border border-slate-300 hover:border-slate-400 shadow-xs"
                  : "bg-slate-50 text-slate-400 border border-slate-200 opacity-50 cursor-not-allowed"
              }`}
              title="Reset all search and status filters"
            >
              <FiRotateCcw className={`w-3.5 h-3.5 ${isFilterActive ? "text-slate-700" : "text-slate-400"}`} />
              <span>Reset</span>
            </button>

            {/* Add Tenant Button (Protected by RBAC) */}
            {canCreate && (
              <button
                type="button"
                onClick={() => navigate(`${MODULE_CONFIG.routeBase}/add`)}
                className="inline-flex items-center justify-center gap-1.5 px-4 h-10 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer whitespace-nowrap active:scale-[0.98]"
              >
                <FiPlus className="w-4 h-4" />
                <span>Add Tenant</span>
              </button>
            )}
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. TENANTS LISTING (STICKY HEADER DESKTOP TABLE)         */}
        {/* ======================================================== */}
        <div className="bg-transparent md:bg-white md:rounded-2xl md:border md:border-slate-300 md:shadow-[0_2px_10px_rgba(0,0,0,0.03)] overflow-visible">
          {/* Loading Indicator */}
          {loading && items.length === 0 ? (
            <div className="py-20 text-center bg-white rounded-2xl border border-slate-300 md:border-0">
              <div className="w-8 h-8 border-3 border-[#115B9D] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-xs font-bold text-slate-900">Loading Tenants...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center space-y-3 p-6 bg-white rounded-2xl border border-slate-300 md:border-0">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 border border-slate-300 text-slate-600 flex items-center justify-center mx-auto mb-2">
                <HiOutlineBuildingOffice2 className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-black text-slate-900">No Tenants Found</h3>
              <p className="text-xs font-semibold text-slate-700 max-w-md mx-auto">
                {debouncedSearch || statusFilter !== "ALL"
                  ? "No tenants match your current search or status filter. Try resetting filters."
                  : "No tenant organizations have been created yet. Click 'Add Tenant' to onboard your first organization."}
              </p>
              <div className="pt-2 flex items-center justify-center gap-3">
                {isFilterActive && (
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold transition-all cursor-pointer border border-slate-300"
                  >
                    <FiRotateCcw className="w-3.5 h-3.5" />
                    <span>Reset Filters</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate(`${MODULE_CONFIG.routeBase}/add`)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer"
                >
                  <FiPlus className="w-3.5 h-3.5" />
                  <span>Add First Tenant</span>
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* DESKTOP TABLE VIEW WITH FIXED STICKY HEADER */}
              <div className="hidden md:block w-full">
                <table className="w-full table-fixed border-collapse text-left text-xs">
                  <colgroup>
                    <col style={{ width: "5%" }} />   {/* 1. Action */}
                    <col style={{ width: "28%" }} />  {/* 2. Tenant / Org */}
                    <col style={{ width: "26%" }} />  {/* 3. Subdomain / Portal */}
                    <col style={{ width: "20%" }} />  {/* 4. Primary Admin */}
                    <col style={{ width: "9%" }} />   {/* 5. Status */}
                    <col style={{ width: "12%" }} />  {/* 6. Created Date */}
                  </colgroup>

                  <thead className="sticky top-[72px] z-30 bg-slate-100 shadow-xs">
                    <tr className="border-b border-slate-300 text-[11px] font-black uppercase tracking-wider text-slate-800 bg-slate-100">
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-3 text-center whitespace-nowrap border-b border-slate-300 shadow-xs rounded-tl-2xl">
                        Action
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-left border-b border-slate-300 shadow-xs">
                        Tenant / Organization
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-left border-b border-slate-300 shadow-xs">
                        Portal URL & Subdomain
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-left border-b border-slate-300 shadow-xs">
                        Admin Contact
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-center border-b border-slate-300 shadow-xs">
                        Status
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-left whitespace-nowrap border-b border-slate-300 shadow-xs rounded-tr-2xl">
                        Created Date
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200">
                    {items.map((item, index) => {
                      const id = item?.id || item?._id;
                      const companyName = item?.company_name || item?.name || "Untitled Tenant";
                      const subdomain = item?.subdomain || "";
                      const portalUrl = getPortalUrl(subdomain);
                      const portalTitle = item?.portal_title || "";
                      // const logo = item?.logo || item?.logoUrl || item?.fileUrl;
                      const rawLogo = item?.logo_url || item?.logo || item?.logoUrl || item?.fileUrl;
                      const logo = getTenantLogoUrl(rawLogo);

                      const adminName =
                        item?.adminFirstName || item?.adminLastName
                          ? `${item?.adminFirstName || ""} ${item?.adminLastName || ""}`.trim()
                          : item?.adminUser?.name || item?.contact_name || "—";
                      const adminEmail =
                        item?.adminEmail || item?.contact_email || item?.support_email || "";
                      const adminPhone =
                        item?.adminPhone || item?.contact_phone || item?.support_phone || "";

                      const status = item?.status || "ACTIVE";
                      const createdAt = item?.createdAt || item?.created_at;

                      const primaryColor = item?.primary_color || "#002B49";
                      const secondaryColor = item?.secondary_color || "#005A9C";
                      const accentColor = item?.accent_color || "#EAA812";

                      return (
                        <tr
                          key={id || `tenant-${index}`}
                          className="hover:bg-slate-50/90 transition-colors group"
                        >
                          {/* 1. Action Menu (Column 1) */}
                          <td className="py-3.5 px-3 text-center">
                            <ActionMenu
                              align="left"
                              actions={[
                                {
                                  label: "View Dossier",
                                  icon: <FiEye className="w-4 h-4 text-slate-800" />,
                                  onClick: () =>
                                    navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`),
                                },
                                canEdit
                                  ? {
                                      label: "Edit Tenant",
                                      icon: <FiEdit3 className="w-4 h-4 text-slate-800" />,
                                      onClick: () =>
                                        navigate(`${MODULE_CONFIG.routeBase}/edit/${id}`),
                                    }
                                  : null,
                                {
                                  label: "Open Portal",
                                  icon: <FiExternalLink className="w-4 h-4 text-[#115B9D]" />,
                                  onClick: () => window.open(portalUrl, "_blank", "noopener,noreferrer"),
                                },
                                canStatusChange
                                  ? {
                                      label: status === "ACTIVE" ? "Suspend Tenant" : "Activate Tenant",
                                      icon: status === "ACTIVE" ? (
                                        <FiAlertOctagon className="w-4 h-4 text-rose-700" />
                                      ) : (
                                        <FiCheckCircle className="w-4 h-4 text-emerald-700" />
                                      ),
                                      onClick: () =>
                                        handleStatusChange(
                                          item,
                                          status === "ACTIVE" ? "SUSPENDED" : "ACTIVE"
                                        ),
                                    }
                                  : null,
                                canDelete
                                  ? {
                                      label: "Delete Tenant",
                                      icon: <FiTrash2 className="w-4 h-4 text-rose-700" />,
                                      onClick: () => handleDelete(item),
                                      isDanger: true,
                                    }
                                  : null,
                              ].filter(Boolean)}
                            />
                          </td>

                          {/* 2. Tenant / Organization */}
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-3 min-w-0">
                              {logo ? (
                                <img
                                  src={logo}
                                  alt={companyName}
                                  className="w-9 h-9 rounded-xl object-contain bg-slate-50 border border-slate-300 shrink-0 p-0.5"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                              ) : (
                                <div
                                  className="w-9 h-9 rounded-xl flex items-center justify-center font-black text-white text-xs shrink-0 shadow-xs"
                                  style={{ backgroundColor: primaryColor }}
                                >
                                  {companyName.slice(0, 2).toUpperCase()}
                                </div>
                              )}
                              <div className="min-w-0 space-y-0.5">
                                <div className="font-bold text-xs sm:text-sm text-slate-900 truncate">
                                  {companyName}
                                </div>
                                {portalTitle && (
                                  <div className="text-[11px] font-medium text-slate-600 truncate max-w-[200px]">
                                    {portalTitle}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* 3. Subdomain / Portal URL */}
                          <td className="py-3.5 px-4">
                            {subdomain ? (
                              <div className="space-y-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-mono font-black text-xs text-[#115B9D] bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                                    {subdomain}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleCopy(portalUrl, "Portal URL")}
                                    className="text-slate-500 hover:text-slate-800 transition-colors p-1 cursor-pointer"
                                    title="Copy Portal URL"
                                  >
                                    <FiCopy className="w-3.5 h-3.5" />
                                  </button>
                                  <a
                                    href={portalUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#115B9D] hover:text-[#0e4b82] transition-colors p-1 cursor-pointer"
                                    title="Open Customer Portal"
                                  >
                                    <FiExternalLink className="w-3.5 h-3.5" />
                                  </a>
                                </div>
                                <div className="text-[10px] font-mono text-slate-600 truncate max-w-[190px]">
                                  {portalUrl.replace("https://", "")}
                                </div>
                              </div>
                            ) : (
                              <span className="text-slate-500 font-medium">—</span>
                            )}
                          </td>

                          {/* 4. Primary Admin Contact */}
                          <td className="py-3.5 px-4">
                            <div className="font-bold text-xs text-slate-900 truncate max-w-[180px]">
                              {adminName}
                            </div>
                            {adminEmail && (
                              <div className="text-[11px] font-medium text-slate-700 truncate max-w-[180px]">
                                {adminEmail}
                              </div>
                            )}
                            {adminPhone && (
                              <div className="text-[10px] font-medium text-slate-600">
                                {adminPhone}
                              </div>
                            )}
                          </td>


                          {/* 6. Status */}
                          <td className="py-3.5 px-4 text-center">
                            {getStatusBadge(status)}
                          </td>

                          {/* 7. Created Date */}
                          <td className="py-3.5 px-4 text-left whitespace-nowrap">
                            <div className="text-xs font-bold text-slate-900">
                              {createdAt ? formatDate(createdAt) : "—"}
                            </div>
                            {createdAt && (
                              <div className="text-[11px] font-medium text-slate-600">
                                {formatTime(createdAt)}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* MOBILE CARDS VIEW (NO OUTER WHITE BOX WRAPPING) */}
              <div className="block md:hidden space-y-3.5">
                {items.map((item, index) => {
                  const id = item?.id || item?._id;
                  const companyName = item?.company_name || item?.name || "Untitled Tenant";
                  const subdomain = item?.subdomain || "";
                  const portalUrl = getPortalUrl(subdomain);
                  const portalTitle = item?.portal_title || "";
                  // const logo = item?.logo || item?.logoUrl || item?.fileUrl;
                  const rawLogo = item?.logo_url || item?.logo || item?.logoUrl || item?.fileUrl;
                  const logo = getTenantLogoUrl(rawLogo);

                  const adminName =
                    item?.adminFirstName || item?.adminLastName
                      ? `${item?.adminFirstName || ""} ${item?.adminLastName || ""}`.trim()
                      : item?.adminUser?.name || item?.contact_name || "—";
                  const adminEmail =
                    item?.adminEmail || item?.contact_email || item?.support_email || "";

                  const status = item?.status || "ACTIVE";
                  const createdAt = item?.createdAt || item?.created_at;

                  const primaryColor = item?.primary_color || "#002B49";

                  return (
                    <div
                      key={id || `tenant-card-${index}`}
                      className="bg-white rounded-2xl p-4 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.03)] space-y-3"
                    >
                      {/* Top Row: Logo/Avatar, Company Name, Action Menu */}
                      <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-2.5">
                        <div className="flex items-center gap-2.5 min-w-0">
                          {logo ? (
                            <img
                              src={logo}
                              alt={companyName}
                              className="w-9 h-9 rounded-xl object-contain bg-slate-50 border border-slate-300 shrink-0 p-0.5"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <div
                              className="w-9 h-9 rounded-xl flex items-center justify-center font-black text-white text-xs shrink-0"
                              style={{ backgroundColor: primaryColor }}
                            >
                              {companyName.slice(0, 2).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <h3 className="font-bold text-xs sm:text-sm text-slate-900 truncate">
                              {companyName}
                            </h3>
                            {portalTitle && (
                              <p className="text-[11px] font-medium text-slate-600 truncate">
                                {portalTitle}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          {getStatusBadge(status)}
                          <ActionMenu
                            align="right"
                            actions={[
                              {
                                label: "View Dossier",
                                icon: <FiEye className="w-4 h-4 text-slate-800" />,
                                onClick: () =>
                                  navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`),
                              },
                              canEdit
                                ? {
                                    label: "Edit Tenant",
                                    icon: <FiEdit3 className="w-4 h-4 text-slate-800" />,
                                    onClick: () =>
                                      navigate(`${MODULE_CONFIG.routeBase}/edit/${id}`),
                                  }
                                : null,
                              {
                                label: "Open Portal",
                                icon: <FiExternalLink className="w-4 h-4 text-[#115B9D]" />,
                                onClick: () => window.open(portalUrl, "_blank", "noopener,noreferrer"),
                              },
                              canStatusChange
                                ? {
                                    label: status === "ACTIVE" ? "Suspend" : "Activate",
                                    icon: <FiCheckCircle className="w-4 h-4 text-slate-800" />,
                                    onClick: () =>
                                      handleStatusChange(
                                        item,
                                        status === "ACTIVE" ? "SUSPENDED" : "ACTIVE"
                                      ),
                                  }
                                : null,
                              canDelete
                                ? {
                                    label: "Delete Tenant",
                                    icon: <FiTrash2 className="w-4 h-4 text-rose-700" />,
                                    onClick: () => handleDelete(item),
                                    isDanger: true,
                                  }
                                : null,
                            ].filter(Boolean)}
                          />
                        </div>
                      </div>

                      {/* Middle: Subdomain & Admin */}
                      <div className="space-y-1.5 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-600">Subdomain:</span>
                          <a
                            href={portalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono font-black text-xs text-[#115B9D] hover:underline flex items-center gap-1"
                          >
                            <span>{subdomain}</span>
                            <FiExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-600">Admin:</span>
                          <span className="font-bold text-slate-900">{adminName}</span>
                        </div>
                        {adminEmail && (
                          <div className="flex items-center justify-between text-[11px]">
                            <span className="font-medium text-slate-600">Email:</span>
                            <span className="font-semibold text-slate-800">{adminEmail}</span>
                          </div>
                        )}
                      </div>

                      {/* Bottom: Date */}
                      <div className="flex items-center justify-between pt-2 border-t border-slate-200 text-xs">
                        <span className="text-[10px] font-bold text-slate-600 uppercase">
                          Created
                        </span>
                        <span className="font-bold text-slate-900">
                          {createdAt ? formatDate(createdAt) : "—"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* PAGINATION CONTROLS (Only visible when total items exceed current limit) */}
              {totalCount > limit && (
                <div className="p-4 bg-white border-t border-slate-200 rounded-b-2xl">
                  <CommonPagination
                    page={page}
                    limit={limit}
                    total={totalCount}
                    onPageChange={(p) => {
                      loader(true);
                      setPage(p);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    onLimitChange={(l) => {
                      loader(true);
                      setLimit(l);
                      setPage(1);
                    }}
                    limitOptions={[25, 50, 100]}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
